/**
 * Parses a numeric input from a string and returns the numeric value.
 * If the input is null, undefined, or an empty string, it returns 0.
 * It handles scientific notation and removes invalid characters.
 *
 * @param input - The input value as a string.
 * @returns The parsed numeric value.
 */
const parseNumericInput = (input: string | null | undefined): number | string => {
	if (input === null || input === undefined || input === '') {
		return 0
	}

	const normalizedInput = String(input).replace(',', '.').trim()

	// Less aggressive sanitization that preserves decimal numbers
	let sanitizedInput = normalizedInput
		.replace(/[^0-9.-]/g, '') // Remove invalid characters
		.replace(/(?!^)-/g, '') // Keep only the first minus sign

	// Handle multiple decimal points - keep only the first one
	const firstDotIndex = sanitizedInput.indexOf('.')
	if (firstDotIndex !== -1) {
		const secondDotIndex = sanitizedInput.indexOf('.', firstDotIndex + 1)
		if (secondDotIndex !== -1) {
			sanitizedInput = sanitizedInput.substring(0, secondDotIndex)
		}
	}

	// Handle special cases
	if (sanitizedInput === '.') return '0.'
	if (sanitizedInput === '-.') return '-0.'
	if (sanitizedInput.startsWith('.')) return `0${sanitizedInput}`

	const numericValue = parseFloat(sanitizedInput)

	// If it's a valid complete number, return the numeric value
	if (!isNaN(numericValue) && !/[.-]$/.test(sanitizedInput)) {
		// Convert -0 to 0 (use +0 to ensure positive zero)
		return numericValue === 0 ? 0 : numericValue
	}

	// If it's still being typed (ends with . or has valid partial format), return as string
	if (/^-?\d*\.?\d*$/.test(sanitizedInput) && sanitizedInput !== '') {
		return sanitizedInput
	}

	// Default to 0 for invalid input
	return 0
}

export default parseNumericInput
